import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./SlipstreamPaywall.module.scss"

/**
 * SlipstreamPaywall — an original, self-contained FEATURE-SPECIFIC paywall for
 * the real built-in **Private Browser**: save photos & videos you find on the
 * open web straight into your private vault, without them ever touching your
 * camera roll, Photos library, or download history.
 *
 * Divergence brief: claude-paywalls/_ledger.md (25 priors). Prior feature
 * concepts heroed break-in alerts (Caught), smart search (Recall) and Face Down
 * Lock (Reflex). None is about *acquiring* media — every one stores / finds /
 * shares / syncs / preserves / hides photos you already have. Slipstream is the
 * first paywall about **getting new media IN, privately, with no trace.**
 *
 *   - Layout:      a working browser window that feeds a one-way CHANNEL down
 *                  into a vault (capture-funnel). Not a centered hero+card, not a
 *                  calendar/road/receipt/map/book/chat/editor/player/network/line.
 *   - Interaction: tap "Save" on a web result and the media SLIPS down the
 *                  channel into the vault (counter climbs, a slot fills) — plus a
 *                  real typed search field that filters the open-web reel. First
 *                  paywall to capture from an external surface into the vault.
 *   - Register:    effortless / slick / quietly-satisfying clean capture.
 *   - Art:         light + photographic (Unsplash = "the open web"); the single
 *                  deep element is the vault (brand navy) so it reads bright-open
 *                  ⇒ sealed-private. Brand blue = the private/safe state.
 *   - Value frame: private capture / import-from-web with NO trace — a trace
 *                  ledger (Camera Roll · Photos · Downloads) stays visibly at 0.
 *
 * Honest & compliant: demonstrates a real feature; no data-loss claim, no fake
 * countdown or scarcity, real savings arithmetic. Yearly is the featured default
 * plan and carries the most prominent price; monthly is small and secondary.
 */

type WebItem = {
  file: string
  label: string
  kind: "photo" | "video"
  tags: string
}

// The open-web reel — things a person finds online and wants to keep privately.
// Photos are Unsplash demo content bundled at build time (public/slipstream/*).
const WEB: WebItem[] = [
  {
    file: "sunset",
    label: "Santorini",
    kind: "photo",
    tags: "travel beach sunset trip",
  },
  {
    file: "coast",
    label: "Big Sur trip",
    kind: "photo",
    tags: "travel road coast mountains trip",
  },
  {
    file: "sneakers",
    label: "The drop",
    kind: "photo",
    tags: "sneakers shoes fashion style",
  },
  {
    file: "street",
    label: "Fit inspo",
    kind: "photo",
    tags: "style outfit fashion",
  },
  {
    file: "interior",
    label: "Studio ideas",
    kind: "photo",
    tags: "interior home design room",
  },
  {
    file: "vinyl",
    label: "Record wall",
    kind: "video",
    tags: "music records vinyl clip video",
  },
  { file: "cafe", label: "That cafe", kind: "photo", tags: "coffee cafe food" },
  {
    file: "garden",
    label: "Balcony plan",
    kind: "photo",
    tags: "garden plants green",
  },
]

const VAULT_SLOTS = 8

type PlanKey = "yearly" | "monthly"

const Globe = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <circle
      cx="12"
      cy="12"
      r="9"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M3 12h18M12 3c2.6 2.4 2.6 15.6 0 18M12 3c-2.6 2.4-2.6 15.6 0 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
    />
  </svg>
)

const SlipstreamPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [query, setQuery] = useState("")

  // saved[i] = true when web item i has been slipped into the vault.
  const [saved, setSaved] = useState<boolean[]>(() => WEB.map(() => false))
  // vault holds the ordered list of files that have landed.
  const [vault, setVault] = useState<string[]>([])
  const [flyer, setFlyer] = useState<{
    file: string
    x: number
    y: number
    w: number
    h: number
    dx: number
    dy: number
    s: number
  } | null>(null)
  const [pulse, setPulse] = useState(false)

  const flightRef = useRef(false)
  const cardRefs = useRef<Array<HTMLDivElement | null>>([])
  const vaultTargetRef = useRef<HTMLDivElement>(null)
  const autoRef = useRef(false)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const idx = WEB.map((_, i) => i)
    if (!q) return idx
    const hit = idx.filter(
      (i) =>
        WEB[i].label.toLowerCase().includes(q) ||
        WEB[i].tags.toLowerCase().includes(q),
    )
    return hit.length ? hit : idx
  }, [query])

  const finalizeSave = useCallback((i: number) => {
    setSaved((s) => {
      if (s[i]) return s
      const next = [...s]
      next[i] = true
      return next
    })
    setVault((v) => (v.includes(WEB[i].file) ? v : [...v, WEB[i].file]))
    setPulse(true)
    window.setTimeout(() => setPulse(false), 720)
  }, [])

  const save = useCallback(
    (i: number) => {
      if (saved[i] || flightRef.current) return
      const cardEl = cardRefs.current[i]
      const targetEl = vaultTargetRef.current

      if (reduceMotion || !cardEl || !targetEl) {
        finalizeSave(i)
        return
      }

      const img = cardEl.querySelector("img")
      const from = (img ?? cardEl).getBoundingClientRect()
      const to = targetEl.getBoundingClientRect()
      const dx = to.left + to.width / 2 - (from.left + from.width / 2)
      const dy = to.top + to.height / 2 - (from.top + from.height / 2)

      flightRef.current = true
      setFlyer({
        file: WEB[i].file,
        x: from.left,
        y: from.top,
        w: from.width,
        h: from.height,
        dx,
        dy,
        s: Math.max(0.28, to.width / from.width),
      })

      window.setTimeout(() => {
        finalizeSave(i)
        setFlyer(null)
        flightRef.current = false
      }, 560)
    },
    [saved, reduceMotion, finalizeSave],
  )

  // Auto-demo once: slip a single result into the vault to teach the gesture,
  // then hand control over.
  useEffect(() => {
    if (autoRef.current) return
    autoRef.current = true
    const t = window.setTimeout(() => save(0), 900)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const savedCount = vault.length

  return (
    <div className={styles.root}>
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 12.1l2 2 3.6-3.9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <span className={styles.eyebrow}>
          <Globe size={14} />
          Private Browser
        </span>
        <h1 className={styles.title}>
          Save it straight to <b>the vault.</b>
        </h1>
        <p className={styles.sub}>
          Browse the web inside Private Photo Vault. Anything you save drops
          into your private albums — never your camera roll.
        </p>
      </div>

      {/* Browser window */}
      <div className={styles.browser}>
        <div className={styles.chrome}>
          <span className={styles.dots} aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <label className={styles.omni}>
            <Globe size={13} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the web — sneakers, travel, recipes…"
              aria-label="Search the web"
              spellCheck={false}
            />
          </label>
        </div>

        <div className={styles.tabline}>
          <span>the open web</span>
          <span className={styles.tablineHint}>tap Save ↓</span>
        </div>

        <div className={styles.web}>
          {filtered.map((i) => {
            const item = WEB[i]
            return (
              <div
                key={item.file}
                ref={(el) => (cardRefs.current[i] = el)}
                className={`${styles.card} ${saved[i] ? styles.cardSaved : ""}`}
              >
                <div className={styles.cardImg}>
                  <img
                    src={`/slipstream/${item.file}.jpg`}
                    alt=""
                    loading="eager"
                    draggable={false}
                  />
                  {saved[i] && (
                    <span className={styles.savedTick} aria-hidden="true">
                      <span>
                        <svg viewBox="0 0 24 24" width="20" height="20">
                          <path
                            d="M5 12.5l4.2 4.3L19 7"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </span>
                  )}
                  <button
                    className={styles.saveBtn}
                    onClick={() => save(i)}
                    disabled={saved[i]}
                    aria-label={`Save ${item.label} to vault`}
                  >
                    {saved[i] ? (
                      "In vault"
                    ) : (
                      <>
                        <svg
                          viewBox="0 0 24 24"
                          width="13"
                          height="13"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 5v14M5 12h14"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                          />
                        </svg>
                        Save
                      </>
                    )}
                  </button>
                </div>
                <div className={styles.cardMeta}>
                  {item.kind === "video" && (
                    <span className={styles.playPip} aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="8" height="8">
                        <path d="M8 5l11 7-11 7z" fill="currentColor" />
                      </svg>
                    </span>
                  )}
                  {item.label}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* One-way channel into the vault */}
      <div className={styles.channel} aria-hidden="true">
        <span className={styles.chute} />
        {[0, 1, 2].map((k) => (
          <motion.span
            key={k}
            className={styles.flow}
            initial={{ top: 2, opacity: 0 }}
            animate={
              reduceMotion
                ? { top: 20, opacity: 0.7 }
                : { top: [2, 40], opacity: [0, 0.9, 0] }
            }
            transition={
              reduceMotion
                ? { duration: 0 }
                : {
                    duration: 1.5,
                    repeat: Infinity,
                    delay: k * 0.5,
                    ease: "easeIn",
                  }
            }
          />
        ))}
        <span className={styles.channelLabel}>
          straight to
          <br />
          your vault
        </span>
      </div>

      {/* The vault */}
      <div className={styles.vault}>
        <div className={styles.vaultTop}>
          <div className={styles.vaultId}>
            <span className={styles.vaultLock} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <rect
                  x="4.5"
                  y="10.5"
                  width="15"
                  height="10"
                  rx="2.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M7.7 10.5V8a4.3 4.3 0 018.6 0v2.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
              </svg>
            </span>
            <span className={styles.vaultName}>
              <b>Your Vault</b>
              <span>Encrypted · Private</span>
            </span>
          </div>
          <div className={styles.counter}>
            <b>{savedCount}</b>
            <span>saved here</span>
          </div>
        </div>

        <div className={styles.grid} ref={vaultTargetRef}>
          {Array.from({ length: VAULT_SLOTS }).map((_, s) => {
            const file = vault[s]
            const isVideo = file
              ? WEB.find((w) => w.file === file)?.kind === "video"
              : false
            return (
              <div
                key={s}
                className={`${styles.slot} ${file ? styles.slotFilled : ""}`}
              >
                {file ? (
                  <>
                    <img
                      src={`/slipstream/${file}.jpg`}
                      alt=""
                      draggable={false}
                    />
                    {isVideo && (
                      <span className={styles.slotVideo} aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="8" height="8">
                          <path d="M8 5l11 7-11 7z" fill="currentColor" />
                        </svg>
                      </span>
                    )}
                    <span className={styles.slotSeal} aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="9" height="9">
                        <path
                          d="M5 12.5l4.2 4.3L19 7"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </>
                ) : (
                  <span className={styles.slotDot} />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Trace ledger — the proof nothing is left behind */}
      <div className={styles.trace}>
        <div className={styles.traceHead}>
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M12 3l7 3v5c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-3z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
          Left behind on your phone
        </div>
        <div className={styles.chips}>
          {["Camera Roll", "Photos app", "Downloads"].map((name) => (
            <div
              key={name}
              className={`${styles.chip} ${pulse ? styles.tracePulse : ""}`}
            >
              <div className={styles.chipTop}>{name}</div>
              <div className={styles.chipZero}>0</div>
              <span className={styles.chipNote}>nothing saved</span>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing — yearly featured, monthly secondary */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.planYearOn : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span
            className={`${styles.radio} ${plan === "yearly" ? styles.radioOn : ""}`}
          >
            {plan === "yearly" && (
              <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5l4.2 4.3L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
          <span className={styles.planBody}>
            <span className={styles.planName}>
              Yearly
              {savingsPct > 0 && (
                <span className={styles.badge}>SAVE {savingsPct}%</span>
              )}
            </span>
            <span className={styles.planTrial}>
              {hasTrial
                ? "7-day free trial, then billed yearly"
                : "Billed once a year"}
            </span>
          </span>
          <span className={styles.planPrice}>
            <b>{yearlyPrice}</b>
            <span>≈ {yearlyPerMonth}/mo</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${plan === "monthly" ? styles.planMonthOn : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          Prefer monthly? <u>{monthlyPrice}/mo</u>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo photos from Unsplash</p>

      {/* Media in transit */}
      {flyer && (
        <motion.img
          className={styles.flyer}
          src={`/slipstream/${flyer.file}.jpg`}
          alt=""
          style={{
            left: flyer.x,
            top: flyer.y,
            width: flyer.w,
            height: flyer.h,
          }}
          initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
          animate={{ x: flyer.dx, y: flyer.dy, scale: flyer.s, opacity: 0.35 }}
          transition={{ duration: 0.56, ease: [0.5, 0, 0.2, 1] }}
        />
      )}

      {/* Sticky CTA */}
      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly" && hasTrial
              ? "Start free trial"
              : "Unlock Private Browser"}
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M5 12h13M12 5l7 7-7 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `${yearlyPrice}/yr after trial · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
          {!reduceMotion && (
            <motion.span
              className={styles.shine}
              initial={{ left: "-55%" }}
              animate={{ left: "120%" }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                repeatDelay: 2.6,
                ease: "easeInOut",
              }}
            />
          )}
        </button>
      </div>
    </div>
  )
}

export default SlipstreamPaywall
